// Joi is imported straight from the package rather than as `v` from `../lib`: these schemas are
// built at module scope, and a value that arrives through the `lib` <-> `store` barrel cycle is
// `undefined` at evaluation time (docs/solutions/store/barrel-import-init-cycle.md).
import Joi from 'joi'

/**
 * The fresh-authentication proof a credential mutation carries so the backend does not revoke
 * every session. It mirrors `currentMfaProof` in the backend's
 * `src/api/util/fresh_authentication_proof.ts`, which is the authoritative contract: a
 * credential-mutation proof is narrower than a login MFA challenge and accepts neither SMS nor
 * email factors.
 */
export type CurrentMfaProof = {type: 'OTP'; verificationCode: string} | {type: 'RECOVERY'; verificationCode: string}

/** The proof fields every credential-mutation endpoint accepts. */
export interface FreshAuthenticationProofPayload {
  currentPassword?: string
  currentMfaProof?: CurrentMfaProof
}

export const CURRENT_PASSWORD_MIN_LENGTH = 8
export const OTP_VERIFICATION_CODE_LENGTH = 6
export const RECOVERY_VERIFICATION_CODE_LENGTH = 12

/**
 * A 6-character code is an authenticator code and a 12-character one is a recovery code;
 * any other length is rejected here, before the request goes out.
 */
export const currentMfaProofSchema = Joi.alternatives(
  Joi.object({
    type: Joi.string().valid('OTP').required(),
    verificationCode: Joi.string().min(OTP_VERIFICATION_CODE_LENGTH).max(OTP_VERIFICATION_CODE_LENGTH).required(),
  }),
  Joi.object({
    type: Joi.string().valid('RECOVERY').required(),
    verificationCode: Joi.string()
      .min(RECOVERY_VERIFICATION_CODE_LENGTH)
      .max(RECOVERY_VERIFICATION_CODE_LENGTH)
      .required(),
  }),
)

/**
 * Spread into the `assertTotalSchema` object of every proof-carrying request saga. Both
 * fields are optional: until the confirm step is switched on, neither is collected, and the
 * backend ignores a proof it did not ask for.
 */
export const freshAuthenticationProofSchemaFragment = {
  currentPassword: Joi.string().min(CURRENT_PASSWORD_MIN_LENGTH).optional(),
  currentMfaProof: currentMfaProofSchema.optional(),
}

export const SCHEMA_ERROR_FALLBACK_MESSAGE = 'payload failed schema validation'

/**
 * The `error` half of a proof-carrying saga's schema-failure REQUEST_ERROR, reduced to `isJoi`
 * and `message`.
 *
 * The message is rebuilt from `details`, NOT taken from `error.message`. `assertTotalSchema` calls
 * `Joi.assert`, which replaces the ValidationError's message with `annotate()` -- the whole
 * rejected payload pretty-printed, `currentPassword` value and all. Each `details[].message` names
 * the field and the constraint it broke ('"currentPassword" length must be at least 8 characters
 * long') and never echoes the submitted value, so those are what a caller gets.
 */
export const toProofSafeSchemaError = (error: unknown): {isJoi: boolean; message: string} => {
  const joiError = error as {isJoi?: boolean; details?: Array<{message?: string}>} | null
  const detailList = Array.isArray(joiError?.details) ? joiError!.details! : []
  const messageList = detailList.map((detail) => detail?.message).filter((message): message is string => !!message)
  return {
    isJoi: Boolean(joiError?.isJoi),
    message: messageList.length ? messageList.join('. ') : SCHEMA_ERROR_FALLBACK_MESSAGE,
  }
}

/**
 * The proof fields, and only the proof fields, ready to spread into a request body -- absent keys
 * stay absent, so a body without a proof is byte-for-byte what it was before this change.
 */
export const freshAuthenticationProofBody = (
  payload: FreshAuthenticationProofPayload,
): FreshAuthenticationProofPayload => ({
  ...(payload.currentPassword === undefined ? {} : {currentPassword: payload.currentPassword}),
  ...(payload.currentMfaProof === undefined ? {} : {currentMfaProof: payload.currentMfaProof}),
})
